import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { modesData, transformationCopy } from "../config/media";

const EASE = [0.16, 1, 0.3, 1];
const N = modesData.length;
const AUTO_MS = 6000;
const SWIPE_PX = 60;

// V1.3 Refinement 09 — interaction-driven mode stage (V1.3 ONLY).
// No scroll pin. Click / arrows / keyboard / drag, slow auto-advance that pauses on hover.
// Media-dominant: one ~82vh stage; copy and navigation live inside the frame.
export const TransformationSectionV13 = () => {
  const stageRef = useRef(null);
  const inView = useInView(stageRef, { amount: 0.5 });
  const [activeIdx, setActiveIdx] = useState(0);
  const [dir, setDir] = useState(1);
  const [paused, setPaused] = useState(false);
  const [cycle, setCycle] = useState(0); // restarts auto-advance progress after manual input
  const active = modesData[activeIdx];

  const goTo = useCallback((i, manual = true) => {
    setActiveIdx((prev) => {
      const next = (i + N) % N;
      setDir(next > prev || (prev === N - 1 && next === 0) ? 1 : -1);
      return next;
    });
    if (manual) setCycle((c) => c + 1);
  }, []);

  const running = inView && !paused;

  useEffect(() => {
    if (!running) return undefined;
    const t = setTimeout(() => goTo(activeIdx + 1, false), AUTO_MS);
    return () => clearTimeout(t);
  }, [running, activeIdx, cycle, goTo]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") goTo(activeIdx + 1);
    if (e.key === "ArrowLeft") goTo(activeIdx - 1);
  };

  const onDragEnd = (_, info) => {
    if (info.offset.x < -SWIPE_PX) goTo(activeIdx + 1);
    else if (info.offset.x > SWIPE_PX) goTo(activeIdx - 1);
  };

  return (
    <section
      id="modes"
      data-testid="transformation-section"
      className="bg-[var(--strollee-dark)] px-6 pb-6 pt-20 text-[var(--strollee-bg)] lg:px-10 lg:pb-8 lg:pt-24"
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-120px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="flex items-end justify-between gap-12 pb-8 lg:pb-10"
      >
        <div className="max-w-[560px]">
          <p
            data-testid="transformation-eyebrow"
            className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8FB89E]"
          >
            {transformationCopy.eyebrow}
          </p>
          <h2 className="mt-4 text-[36px] font-semibold leading-[1.1] tracking-[-0.03em] text-[var(--strollee-bg)] lg:text-[48px]">
            {transformationCopy.headlineLines.map((line) => (
              <span key={line} className="block">
                {line}
              </span>
            ))}
          </h2>
        </div>
        <p className="max-w-[340px] pb-2 text-base leading-relaxed text-[#A3A199]">
          {transformationCopy.support}
        </p>
      </motion.div>

      {/* STAGE */}
      <div
        ref={stageRef}
        data-testid="transformation-stage"
        tabIndex={0}
        role="region"
        aria-roledescription="carousel"
        aria-label="Strollee modes"
        onKeyDown={onKeyDown}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
        className="relative h-[82vh] min-h-[560px] select-none overflow-hidden rounded-[var(--strollee-radius-hero)] bg-[var(--strollee-dark)] outline-none focus-visible:ring-2 focus-visible:ring-[#8FB89E]/60"
      >
        {/* media — directional crossfade, draggable */}
        <motion.div
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.08}
          onDragEnd={onDragEnd}
          className="absolute inset-0 cursor-grab active:cursor-grabbing"
        >
          <AnimatePresence initial={false} custom={dir}>
            <motion.img
              key={active.id}
              custom={dir}
              src={active.image.url}
              alt={active.image.alt}
              data-testid={`mode-media-${active.id}`}
              initial={{ opacity: 0, scale: 1.04, x: dir * 40 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              exit={{ opacity: 0, scale: 1.01, x: dir * -30 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="absolute inset-0 h-full w-full object-cover"
              style={{ objectPosition: active.image.position ?? "center" }}
              draggable={false}
            />
          </AnimatePresence>
        </motion.div>

        {/* scrims */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0F1A15]/85 via-[#0F1A15]/20 to-[#0F1A15]/10" />
        <div className="pointer-events-none absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-[#0F1A15]/45 to-transparent" />

        {/* copy — bottom-left */}
        <div className="pointer-events-none absolute bottom-[112px] left-10 max-w-[520px] lg:bottom-[124px] lg:left-14">
          <AnimatePresence mode="wait">
            <motion.div
              key={active.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.45, ease: EASE }}
            >
              <p className="text-sm font-semibold tracking-[0.2em] text-white/60">
                {active.number}
                <span className="mx-2 text-white/25">/</span>
                <span className="text-white/35">0{N}</span>
              </p>
              <h3
                data-testid="mode-title"
                className="mt-3 text-[56px] font-semibold leading-[0.98] tracking-[-0.03em] text-white lg:text-[80px]"
              >
                {active.name}
              </h3>
              <p
                data-testid="mode-tagline"
                className="mt-4 max-w-[420px] text-lg leading-relaxed text-white/75"
              >
                {active.tagline}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* CTA — bottom-right, permanent */}
        <a
          href="#s1"
          data-testid="transformation-explore-cta"
          className="group absolute bottom-[124px] right-10 inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-6 py-3 text-sm font-semibold tracking-wide text-white backdrop-blur-md transition-all duration-200 hover:-translate-y-[2px] hover:bg-white hover:text-[var(--strollee-ink)] lg:bottom-[136px] lg:right-14"
        >
          {transformationCopy.endCta}
          <ArrowUpRight
            size={16}
            strokeWidth={2.2}
            className="transition-transform duration-200 group-hover:translate-x-[2px] group-hover:-translate-y-[2px]"
          />
        </a>

        {/* NAV — inside the frame: mode tabs with auto-advance progress + arrows */}
        <div className="absolute inset-x-10 bottom-8 flex items-end justify-between gap-10 lg:inset-x-14 lg:bottom-10">
          <div className="flex flex-1 items-end gap-3" role="tablist">
            {modesData.map((mode, i) => {
              const isActive = i === activeIdx;
              return (
                <button
                  key={mode.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  data-testid={`transformation-nav-${mode.id}`}
                  onClick={() => goTo(i)}
                  className="group flex-1 pt-3 text-left"
                >
                  <span
                    className={`block text-[13px] font-semibold tracking-wide transition-colors duration-200 ${
                      isActive ? "text-white" : "text-white/45 group-hover:text-white/80"
                    }`}
                  >
                    {mode.name}
                  </span>
                  <span className="relative mt-3 block h-[2px] w-full overflow-hidden rounded-full bg-white/20">
                    {isActive && (
                      <motion.span
                        key={`${mode.id}-${cycle}`}
                        data-testid="transformation-progress-line"
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: running ? 1 : 0.999 }}
                        transition={{
                          duration: running ? AUTO_MS / 1000 : 0,
                          ease: "linear",
                        }}
                        className="absolute inset-0 origin-left rounded-full bg-[#8FB89E]"
                      />
                    )}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              aria-label="Previous mode"
              data-testid="transformation-prev-btn"
              onClick={() => goTo(activeIdx - 1)}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-white/30 bg-white/5 text-white backdrop-blur-md transition-all duration-200 hover:bg-white hover:text-[var(--strollee-ink)]"
            >
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <button
              type="button"
              aria-label="Next mode"
              data-testid="transformation-next-btn"
              onClick={() => goTo(activeIdx + 1)}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-white/30 bg-white/5 text-white backdrop-blur-md transition-all duration-200 hover:bg-white hover:text-[var(--strollee-ink)]"
            >
              <ArrowRight size={16} strokeWidth={2} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
